import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mail, CheckCircle, Eye, MousePointerClick, AlertTriangle, XCircle,
  RefreshCw, Filter, ArrowRight, LayoutDashboard, Megaphone, TrendingUp, Zap,
  Download
} from 'lucide-react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ── Metric Card ────────────────────────────────────────── */
const MetricCard = ({ label, value, sub, icon: Icon, color, testId }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    className="p-5 rounded-xl bg-white/5 border border-white/10 hover:border-white/20 transition-colors"
    data-testid={testId}
  >
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs text-zinc-500 uppercase tracking-wider font-medium">{label}</span>
      <div className={`w-8 h-8 rounded-lg ${color} flex items-center justify-center`}>
        <Icon className="w-4 h-4 text-white/80" />
      </div>
    </div>
    <p className="text-2xl font-bold text-white tabular-nums">{typeof value === 'number' ? value.toLocaleString() : value}</p>
    {sub && <p className="text-xs text-zinc-500 mt-1">{sub}</p>}
  </motion.div>
);

/* ── Funnel Chart ───────────────────────────────────────── */
const FunnelChart = ({ stages }) => {
  const maxVal = Math.max(...stages.map(s => s.value), 1);
  return (
    <div className="space-y-3" data-testid="delivery-funnel">
      {stages.map((stage, i) => {
        const pct = maxVal > 0 ? (stage.value / maxVal) * 100 : 0;
        const convRate = i > 0 && stages[i - 1].value > 0
          ? ((stage.value / stages[i - 1].value) * 100).toFixed(1)
          : null;
        return (
          <div key={stage.label}>
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2">
                <stage.icon className={`w-3.5 h-3.5 ${stage.textColor}`} />
                <span className="text-xs text-zinc-400 font-medium">{stage.label}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-white tabular-nums">{stage.value.toLocaleString()}</span>
                {convRate && (
                  <span className="text-[10px] text-zinc-600 font-mono">{convRate}%</span>
                )}
              </div>
            </div>
            <div className="h-2 rounded-full bg-white/5 overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${Math.max(pct, 1)}%` }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className={`h-full rounded-full ${stage.barColor}`}
              />
            </div>
            {i < stages.length - 1 && (
              <div className="flex justify-center py-1">
                <ArrowRight className="w-3 h-3 text-zinc-700 rotate-90" />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/* ── CTA Performance Card ───────────────────────────────── */
const CTACard = ({ label, icon: Icon, clicks, totalClicks, color, textColor, testId }) => {
  const pct = totalClicks > 0 ? ((clicks / totalClicks) * 100).toFixed(1) : '0.0';
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      className={`relative p-5 rounded-xl border border-white/10 overflow-hidden`}
      data-testid={testId}
    >
      <div className={`absolute inset-0 ${color} opacity-10`} />
      <div className="relative">
        <div className="flex items-center gap-2 mb-3">
          <Icon className={`w-4 h-4 ${textColor}`} />
          <span className="text-xs text-zinc-400 font-medium">{label}</span>
        </div>
        <p className="text-3xl font-bold text-white tabular-nums">{clicks.toLocaleString()}</p>
        <div className="flex items-center gap-2 mt-2">
          <div className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${pct}%` }}
              transition={{ duration: 0.5 }}
              className={`h-full rounded-full ${color}`}
            />
          </div>
          <span className={`text-xs font-mono ${textColor}`}>{pct}%</span>
        </div>
        <p className="text-[10px] text-zinc-600 mt-1">of all clicks</p>
      </div>
    </motion.div>
  );
};

/* ── Rate Gauge ─────────────────────────────────────────── */
const RateGauge = ({ label, rate, benchmark, color }) => {
  const r = rate || 0;
  const status = r >= (benchmark || 50) ? 'Good' : r >= (benchmark || 50) * 0.5 ? 'Average' : 'Low';
  const statusColor = status === 'Good' ? 'text-emerald-400' : status === 'Average' ? 'text-amber-400' : 'text-red-400';
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs text-zinc-400 font-medium">{label}</span>
        <span className={`text-xs font-medium ${statusColor}`}>{status}</span>
      </div>
      <div className="h-2 rounded-full bg-white/5 overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${Math.min(r, 100)}%` }}
          transition={{ duration: 0.6 }}
          className={`h-full rounded-full ${color}`}
        />
      </div>
      <p className="text-right text-sm font-bold text-white tabular-nums">{r}%</p>
    </div>
  );
};

/* ── Comparison Insight ─────────────────────────────────── */
const ComparisonInsight = ({ dashboardClicks, demoClicks }) => {
  if (dashboardClicks === 0 && demoClicks === 0) return null;
  let text, color;
  if (dashboardClicks > demoClicks) {
    const ratio = demoClicks > 0 ? (dashboardClicks / demoClicks).toFixed(1) : '---';
    text = `Dashboard CTA outperforms Request Demo by ${ratio}x`;
    color = 'text-emerald-400';
  } else if (demoClicks > dashboardClicks) {
    const ratio = dashboardClicks > 0 ? (demoClicks / dashboardClicks).toFixed(1) : '---';
    text = `Request Demo CTA outperforms Dashboard by ${ratio}x`;
    color = 'text-blue-400';
  } else {
    text = 'Dashboard and Request Demo CTAs are performing equally';
    color = 'text-zinc-400';
  }
  return (
    <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white/[0.03] border border-white/5" data-testid="cta-comparison-insight">
      <Zap className={`w-3.5 h-3.5 ${color} shrink-0`} />
      <span className={`text-xs font-medium ${color}`}>{text}</span>
    </div>
  );
};

/* ── CSV Export Helper ──────────────────────────────────── */
const exportMetricsCSV = (data, filter) => {
  if (!data) return;
  const rows = [
    ['Metric', 'Value'],
    ['Filter', filter || 'all'],
    ['Sent', data.totals.sent],
    ['Delivered', data.totals.delivered],
    ['Opened', data.totals.opened],
    ['Clicked', data.totals.clicked],
    ['Bounced', data.totals.bounced],
    ['Dropped', data.totals.dropped],
    ['Delivery Rate', `${data.rates.delivery_rate}%`],
    ['Open Rate', `${data.rates.open_rate}%`],
    ['Click Rate', `${data.rates.click_rate}%`],
    ['Dashboard CTA Clicks', data.cta_clicks.dashboard_cta],
    ['Request Demo CTA Clicks', data.cta_clicks.request_demo_cta],
    ['Other Clicks', data.cta_clicks.other],
  ];
  const csv = rows.map(r => r.join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `email-engagement-${filter || 'all'}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

/* ── Main Component ─────────────────────────────────────── */
export const EmailEngagementDashboard = () => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [emailType, setEmailType] = useState('all');

  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = emailType !== 'all' ? `?email_type=${emailType}` : '';
      const res = await fetch(`${API_URL}/api/admin/email-metrics${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, [token, emailType]);

  useEffect(() => { fetchMetrics(); }, [fetchMetrics]);

  /* ── Loading ── */
  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="engagement-loading">
        <RefreshCw className="w-5 h-5 text-indigo-400 animate-spin" />
        <span className="ml-2 text-zinc-400">Loading email engagement data...</span>
      </div>
    );
  }

  /* ── Error ── */
  if (error && !data) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3" data-testid="engagement-error">
        <AlertTriangle className="w-6 h-6 text-amber-400" />
        <p className="text-sm text-zinc-400">Failed to load metrics: {error}</p>
        <Button variant="outline" size="sm" onClick={fetchMetrics} className="border-white/10 text-zinc-400">
          Retry
        </Button>
      </div>
    );
  }

  /* ── Empty State ── */
  if (data && data.totals.sent === 0 && data.totals.delivered === 0) {
    return (
      <div className="space-y-6" data-testid="email-engagement-dashboard">
        <Header emailType={emailType} setEmailType={setEmailType} loading={loading} onRefresh={fetchMetrics} data={data} />
        <div className="flex flex-col items-center justify-center py-16 gap-3 rounded-xl bg-white/[0.02] border border-white/5" data-testid="engagement-empty">
          <Mail className="w-8 h-8 text-zinc-700" />
          <p className="text-sm text-zinc-500 font-medium">No email engagement data yet</p>
          <p className="text-xs text-zinc-600 max-w-sm text-center">
            Metrics will appear here as users complete onboarding and engage with emails.
          </p>
        </div>
      </div>
    );
  }

  const { totals, rates, cta_clicks } = data;
  const totalCtaClicks = (cta_clicks.dashboard_cta || 0) + (cta_clicks.request_demo_cta || 0) + (cta_clicks.other || 0);
  const clickToOpenRate = totals.opened > 0 ? parseFloat(((totals.clicked / totals.opened) * 100).toFixed(1)) : 0;

  const funnelStages = [
    { label: 'Sent', value: totals.sent, icon: Mail, textColor: 'text-zinc-400', barColor: 'bg-zinc-500' },
    { label: 'Delivered', value: totals.delivered, icon: CheckCircle, textColor: 'text-emerald-400', barColor: 'bg-emerald-500' },
    { label: 'Opened', value: totals.opened, icon: Eye, textColor: 'text-blue-400', barColor: 'bg-blue-500' },
    { label: 'Clicked', value: totals.clicked, icon: MousePointerClick, textColor: 'text-indigo-400', barColor: 'bg-indigo-500' },
  ];

  return (
    <div className="space-y-6" data-testid="email-engagement-dashboard">
      <Header emailType={emailType} setEmailType={setEmailType} loading={loading} onRefresh={fetchMetrics} data={data} />

      {/* ── Metric Cards Grid ── */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <MetricCard label="Emails Sent" value={totals.sent} icon={Mail} color="bg-zinc-600/30" testId="metric-sent" />
        <MetricCard label="Delivered" value={totals.delivered} sub={`${rates.delivery_rate}% delivery`} icon={CheckCircle} color="bg-emerald-500/20" testId="metric-delivered" />
        <MetricCard label="Opens" value={totals.opened} sub={`${rates.open_rate}% open rate`} icon={Eye} color="bg-blue-500/20" testId="metric-opens" />
        <MetricCard label="Clicks" value={totals.clicked} sub={`${rates.click_rate}% click rate`} icon={MousePointerClick} color="bg-indigo-500/20" testId="metric-clicks" />
        <MetricCard label="Bounces / Drops" value={totals.bounced + totals.dropped} sub={`${rates.bounce_rate}% bounce`} icon={AlertTriangle} color="bg-amber-500/20" testId="metric-bounces" />
      </div>

      {/* ── Two-Column: Funnel + CTA Performance ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Delivery Funnel */}
        <div className="p-6 rounded-xl bg-white/5 border border-white/10">
          <h3 className="text-sm font-medium text-white flex items-center gap-2 mb-5">
            <TrendingUp className="w-4 h-4 text-indigo-400" />
            Delivery Funnel
          </h3>
          <FunnelChart stages={funnelStages} />
        </div>

        {/* CTA Performance */}
        <div className="p-6 rounded-xl bg-white/5 border border-white/10">
          <h3 className="text-sm font-medium text-white flex items-center gap-2 mb-5">
            <MousePointerClick className="w-4 h-4 text-indigo-400" />
            CTA Performance
          </h3>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <CTACard
              label="Dashboard CTA"
              icon={LayoutDashboard}
              clicks={cta_clicks.dashboard_cta || 0}
              totalClicks={totalCtaClicks}
              color="bg-emerald-500"
              textColor="text-emerald-400"
              testId="cta-dashboard"
            />
            <CTACard
              label="Request Demo CTA"
              icon={Megaphone}
              clicks={cta_clicks.request_demo_cta || 0}
              totalClicks={totalCtaClicks}
              color="bg-blue-500"
              textColor="text-blue-400"
              testId="cta-demo"
            />
          </div>
          <ComparisonInsight
            dashboardClicks={cta_clicks.dashboard_cta || 0}
            demoClicks={cta_clicks.request_demo_cta || 0}
          />
        </div>
      </div>

      {/* ── Rate Summary ── */}
      <div className="p-6 rounded-xl bg-white/5 border border-white/10">
        <h3 className="text-sm font-medium text-white flex items-center gap-2 mb-5">
          <TrendingUp className="w-4 h-4 text-indigo-400" />
          Rate Summary
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <RateGauge label="Open Rate" rate={rates.open_rate} benchmark={25} color="bg-blue-500" />
          <RateGauge label="Click Rate" rate={rates.click_rate} benchmark={5} color="bg-indigo-500" />
          <RateGauge label="Click-to-Open Rate" rate={clickToOpenRate} benchmark={15} color="bg-violet-500" />
        </div>
      </div>
    </div>
  );
};

/* ── Header Controls ────────────────────────────────────── */
const Header = ({ emailType, setEmailType, loading, onRefresh, data }) => (
  <div className="flex items-center justify-between flex-wrap gap-3">
    <div className="flex items-center gap-2">
      <Filter className="w-4 h-4 text-zinc-400" />
      <Select value={emailType} onValueChange={setEmailType}>
        <SelectTrigger className="w-[220px] bg-white/5 border-white/10 text-white text-xs h-8" data-testid="engagement-type-filter">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Email Types</SelectItem>
          <SelectItem value="onboarding_completion">Onboarding Completion</SelectItem>
        </SelectContent>
      </Select>
    </div>
    <div className="flex items-center gap-2">
      {data && (
        <Button
          variant="outline" size="sm"
          onClick={() => exportMetricsCSV(data, emailType)}
          className="border-white/10 text-zinc-400 hover:text-white hover:bg-white/5 h-8"
          data-testid="engagement-export-csv"
        >
          <Download className="w-3.5 h-3.5 mr-1.5" />
          Export CSV
        </Button>
      )}
      <Button
        variant="outline" size="sm"
        onClick={onRefresh}
        className="border-white/10 text-zinc-400 hover:text-white hover:bg-white/5 h-8"
        data-testid="engagement-refresh"
      >
        <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
        Refresh
      </Button>
    </div>
  </div>
);
